/**
 * Wishlist counter + drawer/modal opener for the MIB Wishlist app.
 *
 * The app manages `data-mib-wishlist-total-count` elements and broadcasts
 * `mib:wishlist:items`. This mirrors whichever of the two arrives onto the
 * theme's badge markup (`hide` / `more-than-9` / `more-than-99` on the
 * surrounding `.count-container`) and turns `[data-mib-wishlist-open]` links
 * into openers for the app's built-in drawer or modal.
 */
(() => {
  const COUNT_ATTR = 'data-mib-wishlist-total-count';
  // Only the badges this theme renders, so counters the app injects into its
  // own drawer/modal keep being managed by the app alone.
  const COUNT_SELECTOR = '[data-mib-wishlist-count-target]';
  const OPEN_SELECTOR = '[data-mib-wishlist-open]';

  const toNumber = (value) => {
    const digits = String(value ?? '').replace(/[^\d]/g, '');
    return digits === '' ? null : parseInt(digits, 10);
  };

  const readCount = (el, preferAttribute) => {
    const sources = preferAttribute
      ? [el.getAttribute(COUNT_ATTR), el.textContent]
      : [el.textContent, el.getAttribute(COUNT_ATTR)];

    for (const source of sources) {
      const parsed = toNumber(source);
      if (parsed !== null) return parsed;
    }

    return 0;
  };

  const render = (el, count) => {
    const value = String(count);

    // Only write when something actually changed, otherwise the observer below
    // keeps re-triggering itself.
    if (el.getAttribute(COUNT_ATTR) !== value) el.setAttribute(COUNT_ATTR, value);
    if (el.textContent !== value) el.textContent = value;

    // BELLER 16.10.2026: a non-empty wishlist fills the header heart instead of
    // showing a number, so the cart keeps the only count in the header.
    const link = el.closest('.mib-wishlist-count');
    if (link) link.classList.toggle('bms-serce-pelne', count > 0);

    const container = el.closest('.count-container');
    if (!container) return;

    container.classList.toggle('hide', count < 1);
    container.classList.toggle('more-than-9', count > 9 && count < 100);
    container.classList.toggle('more-than-99', count > 99);
  };

  const initCounter = (el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.dataset.mibWishlistCountInit === '1') return;
    el.dataset.mibWishlistCountInit = '1';

    render(el, readCount(el, false));

    const mo = new MutationObserver((records) => {
      const fromAttribute = records.some((record) => record.type === 'attributes');
      render(el, readCount(el, fromAttribute));
    });

    mo.observe(el, {
      attributes: true,
      attributeFilter: [COUNT_ATTR],
      childList: true,
      characterData: true,
      subtree: true,
    });
  };

  const scan = (root = document) => {
    root.querySelectorAll?.(COUNT_SELECTOR).forEach(initCounter);
  };

  scan();

  const globalMO = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches?.(COUNT_SELECTOR)) initCounter(node);
        scan(node);
      }
    }
  });

  globalMO.observe(document.documentElement, { childList: true, subtree: true });

  window.addEventListener('mib:wishlist:items', (e) => {
    const detail = e?.detail;

    let count = typeof detail?.count === 'number' ? detail.count : null;
    if (count === null && Array.isArray(detail?.items)) count = detail.items.length;
    if (count === null) return;

    document.querySelectorAll(COUNT_SELECTOR).forEach((el) => {
      initCounter(el);
      render(el, count);
    });
  });

  // The triggers stay real links so they work without JS and with modifier
  // clicks; a plain click hands over to the app's own drawer or modal instead.
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const trigger = e.target instanceof Element ? e.target.closest(OPEN_SELECTOR) : null;
    if (!trigger) return;

    const kind = trigger.getAttribute('data-mib-wishlist-open');
    if (kind !== 'drawer' && kind !== 'modal') return;

    e.preventDefault();
    window.dispatchEvent(new CustomEvent('mib:wishlist:open', { detail: { kind } }));
  });
})();
